import { useCallback } from 'react';
import type { PermissionsHookProps } from '@web-config-app/core';
import { usePermissions } from '../use-permissions/use-permissions';

interface UseHasPermissionsHelpersProps extends PermissionsHookProps {}

/**
 * This hook is used to help with checking permissions.
 *
 * @param props.environmentKey - The environment to fetch permissions from
 * @param props.tenantId - The tenant ID for cache isolation
 * @param props.isAuthenticated - Whether the user is authenticated (enables the query when true)
 * @returns An object containing the hasPermission, hasAllPermissions, and hasSomePermissions functions.
 */
export const useHasPermissionsHelpers = ({
  environmentKey,
  tenantId,
  isAuthenticated,
}: UseHasPermissionsHelpersProps) => {
  const permissions = usePermissions({
    environmentKey,
    tenantId,
    isAuthenticated,
  });

  /**
   * This function will check a requested permission against the list of available permissions. If the requested permission is included in the user's list of permissions, it will return true. Otherwise, it returns false.
   *
   * @param permissionToCheck string value containing a permission to check against the user's list of permissions
   * @returns boolean
   */
  const hasPermission = useCallback(
    (permissionToCheck: string) => permissions.includes(permissionToCheck),
    [permissions],
  );

  /**
   * This function will check a list of requested permissions against the list of the user's available permissions. If the requested permissions are all included in the user's list of permissions, it will return true. Otherwise, it returns false.
   *
   * @param permissionsToCheck string array containing a list of values to check against the user's list of permissions
   * @returns boolean
   */
  const hasAllPermissions = useCallback(
    (permissionsToCheck: string[]) =>
      permissionsToCheck.every((permission) =>
        permissions.includes(permission),
      ),
    [permissions],
  );

  /**
   * This function will check a list of requested permissions against the list of the user's available permissions. If at least one of the requested permissions is included in the user's list of permissions, it will return true. Otherwise, it returns false.
   *
   * @param permissionsToCheck string array containing a list of values to check against the user's list of permissions
   * @returns boolean
   */
  const hasSomePermissions = useCallback(
    (permissionsToCheck: string[]) =>
      permissionsToCheck.some((permission) => permissions.includes(permission)),
    [permissions],
  );

  return {
    hasPermission,
    hasAllPermissions,
    hasSomePermissions,
  };
};
